import React, { useContext, useEffect, useState } from 'react';
import { getPublicServerInfo } from '@go-client/lib/api/info';
import { BubbleOverlay } from '@go-client/components/BubbleUI';
import { PublicServerInfo } from '@go-shared/types/info';

const PUBLIC_SERVER_INFO_POLLING_SECONDS = 900; // 15 minutes

const PublicServerInfoContext = React.createContext<PublicServerInfo | undefined>(undefined);

export function usePublicServerInfo() {
  const context = useContext(PublicServerInfoContext);

  if (context === undefined) {
    throw new Error('usePublicServerInfo must be used within a PublicServerInfoProvider');
  }

  return context;
}

export function WithPublicServerInfo(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className: propClasses, children, ...rest } = props;

  const [publicServerInfo, setPublicServerInfo] = useState<PublicServerInfo | undefined>(undefined);

  // WARNING: If you would like to use state to further complex this refresh function,
  // see UserInfoContext.tsx for an example of how you can do this.
  async function fetchServerInfo() {
    const psi = await getPublicServerInfo();
    setPublicServerInfo(psi);
  }

  useEffect(() => {
    fetchServerInfo().catch(console.error);

    const interval = setInterval(() => {
      fetchServerInfo().catch(console.error);
    }, 1000 * PUBLIC_SERVER_INFO_POLLING_SECONDS);

    return () => clearInterval(interval);
  }, []);

  const classes = ['h-full'];
  if (propClasses) classes.push(propClasses);

  return (
    <BubbleOverlay loading={!publicServerInfo} {...rest} className={classes.join(' ')}>
      {publicServerInfo && (
        <PublicServerInfoProvider publicServerInfo={publicServerInfo}>
          {props.children}
        </PublicServerInfoProvider>
      )}
    </BubbleOverlay>
  );
}

interface PublicServerInfoProviderPropsBase {
  publicServerInfo: PublicServerInfo;
}

type PublicServerInfoProviderProps = PublicServerInfoProviderPropsBase & {
  children?: React.ReactNode;
};

export default function PublicServerInfoProvider(props: PublicServerInfoProviderProps) {
  const { publicServerInfo, children } = props;

  return (
    <PublicServerInfoContext.Provider value={publicServerInfo}>
      {children}
    </PublicServerInfoContext.Provider>
  );
}
